import { Application, Feature } from '@frontend/functionality/types';
import { IUser } from '@frontend/user/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../../../hooks';
import { useAppDispatch } from '../../../redux/store';
import { useLicenseRepository } from '../../../utils/license-repository';
import { useItsmeQrAuthenticationFlow } from '../../hooks/useItsmeQrAuthenticationFlow';
import { authenticateKioskUser } from '../../utils/authenticate-kiosk-user';
import { useScreen } from '../useScreen';
import { useProveIdentityVerification } from './use-prove-identity-verification';

const SENSOR_TIMEOUT = 10000;

export type ProveIdentityMode = 'both' | 'badge' | 'itsme';

type AuthMethod = 'badge' | 'itsme';

interface ViewProps {
    phase: ReturnType<typeof useProveIdentityVerification>['phase'];
    authMethod: AuthMethod;
    showAuthMethodToggle: boolean;
    showBadgeAuth: boolean;
    showItsmeAuth: boolean;
    isProcessing: boolean;
    isIdentifying: boolean;
    accessDenied: boolean;
    accessDeniedMessage: string;
    alreadyAuthenticated: boolean;
    error: string | null;
    itsmeViewProps: ReturnType<typeof useItsmeQrAuthenticationFlow>;
    resetError: () => void;
    onSelectBadgeAuth: () => void;
    onSelectItsmeAuth: () => void;
}

const useProveIdentity = (mode: ProveIdentityMode = 'both'): ViewProps => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const { navigationState } = useScreen();
    const { getFunctionality } = useLicenseRepository();
    const {
        phase,
        isProcessing,
        accessDenied,
        accessDeniedMessage,
        alreadyAuthenticated,
        error,
        resetError,
        verifyUserAccess,
        setError,
        setIsProcessing,
        identificationErrorMessage,
        authenticationFailedMessage
    } = useProveIdentityVerification();

    const allowBadge = mode === 'both' || mode === 'badge';
    const allowItsme = mode === 'both' || mode === 'itsme';
    const initialAuthMethod: AuthMethod = mode === 'itsme' ? 'itsme' : 'badge';

    const [authMethod, setAuthMethod] = useState<AuthMethod>(initialAuthMethod);
    const [isIdentifying, setIsIdentifying] = useState(false);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const itsmeLicensed = !!getFunctionality(Application.ITSME, Feature.QR_CODE);
    const showAuthMethodToggle = mode === 'both' && itsmeLicensed;

    const clearSensorTimeout = useCallback(() => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
    }, []);

    const handleAuthenticatedUser = useCallback(
        (user: IUser) => {
            void verifyUserAccess(user.id);
        },
        [verifyUserAccess]
    );

    const itsmeViewProps = useItsmeQrAuthenticationFlow({
        id: 'prove-identity-itsme',
        enabled: allowItsme && authMethod === 'itsme' && phase === 'authenticating' && !navigationState.user,
        onUserAuthenticated: handleAuthenticatedUser
    });

    const authenticateBadgeUser = useCallback(
        async (user: IUser) => {
            if (!navigationState.iot) {
                setError(authenticationFailedMessage);
                return;
            }

            setIsProcessing(true);
            setError(null);

            const success = await authenticateKioskUser(dispatch, navigationState.iot.account_id, navigationState.iot.id, user);
            if (!success) {
                setError(authenticationFailedMessage);
                setIsProcessing(false);
                return;
            }

            setIsProcessing(false);
            await verifyUserAccess(user.id);
        },
        [authenticationFailedMessage, dispatch, navigationState.iot, setError, setIsProcessing, verifyUserAccess]
    );

    useScanners({
        id: 'PROVE_IDENTITY',
        onDeviceSensorReceived: () => {
            if (!allowBadge || authMethod !== 'badge' || navigationState.user || phase !== 'authenticating') return;
            setIsIdentifying(true);
            clearSensorTimeout();
            sensorTimeoutRef.current = setTimeout(() => {
                setIsIdentifying(false);
                setError(identificationErrorMessage);
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
        }
    });

    useIdentification({
        id: 'PROVE_IDENTITY',
        onNoMatchingIdentificationFound: () => {
            if (!allowBadge || authMethod !== 'badge' || navigationState.user || phase !== 'authenticating') return;
            clearSensorTimeout();
            setIsIdentifying(false);
            setError(identificationErrorMessage);
        },
        onUnlinkedIdentificationFound: () => {
            if (!allowBadge || authMethod !== 'badge' || navigationState.user || phase !== 'authenticating') return;
            clearSensorTimeout();
            setIsIdentifying(false);
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.prove-identity.unlinked-identification',
                    description: 'Error shown when a scanned badge is recognized but not linked to a user',
                    defaultMessage: 'This badge was recognized but is not linked to a user yet. Please try a different badge or contact support.'
                })
            );
        },
        onUserIdentified: (user) => {
            if (!allowBadge || authMethod !== 'badge' || navigationState.user || phase !== 'authenticating') return;
            clearSensorTimeout();
            setIsIdentifying(false);
            void authenticateBadgeUser(user);
        }
    });

    useEffect(() => {
        return () => {
            clearSensorTimeout();
        };
    }, [clearSensorTimeout]);

    return {
        phase,
        authMethod,
        showAuthMethodToggle,
        showBadgeAuth: allowBadge && phase === 'authenticating' && authMethod === 'badge' && !navigationState.user && !isProcessing && !isIdentifying,
        showItsmeAuth: allowItsme && phase === 'authenticating' && authMethod === 'itsme' && !navigationState.user && !isProcessing,
        isProcessing,
        isIdentifying,
        accessDenied,
        accessDeniedMessage,
        alreadyAuthenticated,
        error,
        itsmeViewProps,
        resetError,
        onSelectBadgeAuth: () => {
            if (mode === 'both') setAuthMethod('badge');
        },
        onSelectItsmeAuth: () => {
            if (mode === 'both') setAuthMethod('itsme');
        }
    };
};

export default useProveIdentity;
